import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleField, FieldHelp, LabelWithHelp, SecretPasswordField } from '../../../components';
import { Field } from 'formik';

interface TicketingFormProps {};

const fieldClassName = "block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

function TicketingForm(props: TicketingFormProps) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <div className="mt-5">
                    <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                        {t('settings.ticketing')}
                        <FieldHelp content={t('settings.help.ticketing_urls') || ''} />
                    </h2>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="openid" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_ticketing')}
                        </label>
                        <ToggleField name="ticketing.enabled" className="inline"/>
                    </div>
                </div>
                <div className="mt-5">
                    <LabelWithHelp htmlFor="ticket_url" help={t('settings.help.ticketing_urls') || ''}>
                        {t('settings.ticketing_ticket_url')}
                    </LabelWithHelp>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="ticketing.ticket_url"
                            className={fieldClassName}
                            placeholder="https://tickets.net/showTicket?id=%d"
                        />
                        <small>{ t('settings.ticketing_ticket_url_desc') }</small>
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="reply_url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.ticketing_reply_url')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="ticketing.reply_url"
                            className={fieldClassName}
                            placeholder="https://tickets.net/showTicket?id=%d"
                        />
                        <small>{ t('settings.ticketing_reply_url_desc') }</small>
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="close_url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.ticketing_close_url')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="ticketing.close_url"
                            className={fieldClassName}
                            placeholder="https://tickets.net/showTicket?id=%d"
                        />
                        <small>{ t('settings.ticketing_close_url_desc') }</small>
                    </div>
                </div>

                <div className="mt-10 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h2 className="mb-1 text-base font-semibold text-lg text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                        {t('settings.rt')}
                        <FieldHelp content={t('settings.help.rt') || ''} />
                    </h2>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="rt-enabled" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_rt')}
                        </label>
                        <ToggleField name="rt.enabled" className="inline"/>
                    </div>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-base-url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_base_url')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="rt.base_url"
                            className={fieldClassName}
                            placeholder="https://rt.example.com"
                        />
                        <small>{t('settings.rt_base_url_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-token" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_token')}
                    </label>
                    <div className="mt-2">
                        <SecretPasswordField
                            name="rt.token"
                            hasFlagName="rt.hasToken"
                            className={fieldClassName}
                        />
                        <small>{t('settings.rt_token_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <div className="mt-1 flex flex-col lg:mt-0 lg:flex-row lg:flex-wrap lg:space-x-6">
                        <div className="flex-1 w-100">
                            <label htmlFor="rt-username" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.rt_username')}
                            </label>
                            <div className="mt-2">
                                <Field
                                    type="text"
                                    name="rt.username"
                                    className={fieldClassName}
                                    placeholder="rt-user"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                        <div className="mt-5 lg:mt-0 flex-1 w-100">
                            <label htmlFor="rt-password" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.rt_password')}
                            </label>
                            <div className="mt-2">
                                <SecretPasswordField
                                    name="rt.password"
                                    hasFlagName="rt.hasPassword"
                                    className={fieldClassName}
                                />
                            </div>
                        </div>
                    </div>
                    <small className="mt-1 block">{t('settings.rt_auth_desc')}</small>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-ticket-path" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_ticket_path_template')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="rt.ticket_path_template"
                            className={fieldClassName}
                            placeholder="/Ticket/Display.html?id=%s"
                        />
                        <small>{t('settings.rt_ticket_path_template_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-default-status" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_default_post_import_status')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="rt.default_post_import_status"
                            className={fieldClassName}
                            placeholder="resolved"
                        />
                        <small>{t('settings.rt_default_post_import_status_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-statuses" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_statuses')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="rt.statuses"
                            className={fieldClassName}
                            placeholder="new, open, stalled, resolved, rejected, deleted"
                        />
                        <small>{t('settings.rt_statuses_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-migrate-template" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_migrate_comment_template')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="rt.migrate_comment_template"
                            className={fieldClassName}
                            placeholder="Migré vers Yoda #%s — %s"
                        />
                        <small>{t('settings.rt_migrate_comment_template_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <label htmlFor="rt-migrate-marker" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.rt_migrate_comment_marker')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="rt.migrate_comment_marker"
                            className={fieldClassName}
                            placeholder="Migré vers Yoda #"
                        />
                        <small>{t('settings.rt_migrate_comment_marker_desc')}</small>
                    </div>
                </div>

                <div className="mt-5">
                    <div className="mt-1 flex flex-col lg:mt-0 lg:flex-row lg:flex-wrap lg:space-x-6">
                        <div className="flex-1 w-100">
                            <label htmlFor="rt-fetch-concurrency" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.rt_fetch_concurrency')}
                            </label>
                            <div className="mt-2">
                                <Field
                                    type="number"
                                    min={1}
                                    name="rt.fetch_concurrency"
                                    className={fieldClassName}
                                    placeholder="5"
                                />
                            </div>
                        </div>
                        <div className="mt-5 lg:mt-0 flex-1 w-100">
                            <label htmlFor="rt-import-concurrency" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.rt_import_concurrency')}
                            </label>
                            <div className="mt-2">
                                <Field
                                    type="number"
                                    min={1}
                                    max={10}
                                    name="rt.import_concurrency"
                                    className={fieldClassName}
                                    placeholder="2"
                                />
                            </div>
                        </div>
                        <div className="mt-5 lg:mt-0 flex-1 w-100">
                            <label htmlFor="rt-timeout" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.rt_timeout_ms')}
                            </label>
                            <div className="mt-2">
                                <Field
                                    type="number"
                                    min={1000}
                                    step={1000}
                                    name="rt.timeout_ms"
                                    className={fieldClassName}
                                    placeholder="30000"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mt-5">
                        <label htmlFor="rt-max-batch-size" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.rt_max_batch_size')}
                        </label>
                        <div className="mt-2">
                            <Field
                                type="number"
                                min={1}
                                name="rt.max_batch_size"
                                className={fieldClassName}
                                placeholder="500"
                            />
                        </div>
                    </div>
                    <small className="mt-1 block">{t('settings.rt_perf_desc')}</small>
                </div>
            </div>
        </div>
    );
}

export default TicketingForm;
